// @ts-check
import React, { useEffect, useState, useRef } from "react"
import RegisteredTasksDataTable from "./table/IndividualReportTableContainer.controller"
import ContextMenu from "../../../components/atoms/ContextMenu.atom"
import ModalFormEditTask from "../../../components/compounds/ModalFormEditTask.compound"
import useToast from "../../../hooks/useToast.hook"
import { useNavigate } from "react-router-dom"
import ModalFormCreateTask from "../../../components/compounds/ModalFormCreateTask.compound"
import { AppRoutes } from "../../../consts/appRoutes.consts"
import ModalBulkEditTasks from "../../../components/compounds/ModalBulkEditTasks/ModalBulkEditTasks.compound"
import { IndeterminateCheckBox } from "../../../components/atoms/icons/IndeterminateCheckBox.atom"
import useIndividualReportFiltersContext from "../../../hooks/contextConsumers/useIndividualReportFiltersContext.hook"

/**
 * @typedef {import("../../../services/reports.service.types").IndividualTaskReportItem} IndividualTaskReportItem
 */

const RegisteredTasks = React.forwardRef(
  /**
   * @param {*} props
   * @param {React.ForwardedRef<import("./table/IndividualReportTableContainer.controller.types").IndividualReportTableContainerRef | null>} ref
   */
  (props, ref) => {
    const {
      openCreateTaskForTeamMemberModal,
      setOpenCreateTaskForTeamMemberModal,
      highlightedTask,
      loading,
      setLoading,
    } = props

    const contextMenuRef = useRef(null)
    const [contextMenuTarget, setContextMenuTarget] = useState(
      /** @type {IndividualTaskReportItem | null} */ (null)
    )
    const [selectedTasks, setSelectedTasks] = useState(
      /** @type {IndividualTaskReportItem[]} */ ([])
    )
    const [updatingTask, setUpdatingTask] = useState(
      /** @type {IndividualTaskReportItem | null} */ (null)
    )
    const [editTaskVisible, setEditTaskVisible] = useState(false)
    const [bulkEditingTasksModalVisible, setBulkEditingTasksModalVisible] =
      useState(false)
    const [inSelectionMode, setInSelectionMode] = useState(false)

    const { setErrorMessage } = useToast()
    const navigate = useNavigate()

    const { teamMember, dateRange, period } =
      useIndividualReportFiltersContext()

    const teamMemberFullName = `${teamMember.value?.name || ""} ${teamMember.value?.lastName || ""}`

    useEffect(() => {
      if (ref && typeof ref !== "function") {
        ref.current?.reset()
      }
    }, [teamMember.value, dateRange, period])

    if (!teamMember.value) return null

    return (
      <>
        <RegisteredTasksDataTable
          ref={ref}
          contextMenuRef={contextMenuRef}
          setContextMenuTarget={setContextMenuTarget}
          setSelectedTasks={setSelectedTasks}
          updatingTask={updatingTask}
          setLoading={setLoading}
          loading={loading}
          highlightedTask={highlightedTask}
          inSelectionMode={inSelectionMode}
        />
        <ContextMenu
          ref={contextMenuRef}
          className="context-menu-individual-report"
          model={(() => {
            if (!contextMenuTarget) return []

            /** @type {import("primereact/menuitem").MenuItem[]} */
            const cmItems = []

            const rightClickedTaskIsSelected = selectedTasks.find(
              (task) => task.taskId === contextMenuTarget.taskId
            )

            if (inSelectionMode) {
              cmItems.push({
                template: (
                  <div
                    style={{
                      borderBottom: "thin solid rgb(237, 240, 243)",
                    }}
                    className="p-2 px-3"
                  >
                    {selectedTasks.length} tarea
                    {selectedTasks.length === 0 ? "s" : ""}
                    {selectedTasks.length > 1 ? "s" : ""} seleccionada
                    {selectedTasks.length === 0 ? "s" : ""}
                    {selectedTasks.length > 1 ? "s" : ""}
                  </div>
                ),
              })
              if (selectedTasks.length > 0) {
                cmItems.push({
                  label: "Editar selección",
                  icon: "pi pi-pencil",
                  command: async () => {
                    setBulkEditingTasksModalVisible(true)
                  },
                })
              }
              if (rightClickedTaskIsSelected) {
                cmItems.push({
                  label: "Deseleccionar",
                  icon: (
                    <IndeterminateCheckBox className="indeterminate-check-box-icon" />
                  ),
                  command: () => {
                    if (ref && typeof ref !== "function") {
                      ref.current?.unselectRow(contextMenuTarget.taskId)
                    }
                  },
                })
              }
            } else {
              cmItems.push({
                label: "Editar",
                icon: "pi pi-pencil",
                command: async () => {
                  setEditTaskVisible(true)
                },
              })
              cmItems.push({
                label: "Selección múltiple",
                icon: "pi pi-check",
                command: () => {
                  setInSelectionMode(true)
                },
              })
            }

            if (inSelectionMode) {
              cmItems.push({
                label: "Cancelar selección",
                icon: "pi pi-times-circle",
                command: () => {
                  setInSelectionMode(false)
                  setSelectedTasks([])
                },
              })
            }

            if (!contextMenuTarget?.hasChangelog) {
              return cmItems
            }

            if (!inSelectionMode) {
              cmItems.push({
                label: "Historial de cambios",
                icon: "pi pi-history",
                command: () => {
                  navigate(AppRoutes.historialCambios.index, {
                    state: { taskId: contextMenuTarget?.taskId },
                  })
                },
              })
            }

            return cmItems
          })()}
        />
        <ModalBulkEditTasks
          visible={bulkEditingTasksModalVisible}
          setVisible={setBulkEditingTasksModalVisible}
          selectedTasks={selectedTasks}
          memberFullName={teamMemberFullName}
          onEditCompleted={
            /** @param {string | undefined} errorMessage */
            (errorMessage) => {
              if (errorMessage) {
                setErrorMessage({ message: errorMessage })
              }
              setBulkEditingTasksModalVisible(false)
              setUpdatingTask(null)
              setSelectedTasks([])
              setInSelectionMode(false)
              if (ref && typeof ref !== "function") {
                ref.current?.refresh()
              }
            }
          }
        />
        <ModalFormEditTask
          visible={editTaskVisible}
          setVisible={setEditTaskVisible}
          task={contextMenuTarget ?? undefined}
          teamMemberId={teamMember.value?.id}
          onSubmit={() => {
            setUpdatingTask(contextMenuTarget)
          }}
          onEditCompleted={(errorMessage) => {
            if (errorMessage) {
              setErrorMessage({ message: errorMessage })
            }
            setEditTaskVisible(false)
            setUpdatingTask(null)

            if (ref && typeof ref !== "function") {
              ref.current?.refresh()
            }
          }}
          onlyActiveProjects={false}
          isAdminEdit
          waitForResponse={false}
        />

        <ModalFormCreateTask
          visible={openCreateTaskForTeamMemberModal}
          setVisible={setOpenCreateTaskForTeamMemberModal}
          teamMember={teamMember.value}
          onCreateCompleted={
            /** @param {string | undefined} errorMessage */
            (errorMessage) => {
              if (errorMessage) {
                setErrorMessage({ message: errorMessage })
              }
              setLoading(false)
            }
          }
          onSubmit={() => {
            setLoading(true)

            if (ref && typeof ref !== "function") {
              ref.current?.refresh()
            }
          }}
        />
      </>
    )
  }
)

export default RegisteredTasks
